import React, { useCallback, useEffect, useState } from "react";
import {
  Dialog,
  Box,
  Grid,
  Typography,
} from "@mui/material";
import Button from "../../common/Button";
import CustomTextField from "../../common/CustomTextField";
import { useForm } from "react-hook-form";
import { ProductCommissionAgency, Option, AgencyOption } from "../../../redux/slice/agency/AgencySlice";
import CustomDatePicker from "../../common/CustomDatePicker";
import CustomDropdownField from "../../common/CustomDropdownFiled";
import CustomMultipleDropDown from "../../common/CustomMultipleDropDown";
import CancelIcon from "@mui/icons-material/Cancel";
import { useModalStyles } from "../../common/useModalStyles";
import { PackageSearch, Save } from "lucide-react";

interface Row {
  productName: string | undefined;
  effectiveDate: string | undefined;
  inactiveDate: string | undefined;
  commissionRate: number | string | undefined;
  productId: number | number[] | undefined;
}

interface AddProductCommissionModalProps {
  open: boolean;
  productOptions: Option[];
  selectedAssociateTypeOption: any;
  setSelectedAssociateType: any;
  associateAgencyOptions: AgencyOption[];
  associateOptions: any;
  practiceListOptions: any;
  agencyExecutivesOptions: any;
  isCommTypeOverride: boolean;
  editCommission: boolean;
  defaultValue?: Row;
  handleClose: () => void;
  submitCommission: (commission: ProductCommissionAgency) => void;
  openFrom?: string;
}

const AddProductCommissionModal: React.FC<AddProductCommissionModalProps> = ({
  open,
  productOptions,
  associateAgencyOptions,
  agencyExecutivesOptions,
  associateOptions,
  practiceListOptions,
  isCommTypeOverride,
  selectedAssociateTypeOption,
  setSelectedAssociateType,
  editCommission,
  defaultValue,
  handleClose,
  submitCommission,
  openFrom,
}) => {
  const { control, handleSubmit, reset, setValue } = useForm<ProductCommissionAgency>();

  const onCloseAction = () => {
    handleClose();
    reset();
    setSelectedAssociateType("");
  };

  const {
    paperSx,
    headerSx,
    bodySx,
    footerSx,
    tokens,
    cancelBtnSx,
    primaryBtnSx,
    dropdownWrapSx,
    inputFieldSx,
    datepickerWrapSx,
    iconBoxSx,
  } = useModalStyles();

  // ── Reusable FieldLabel (same as AddPatientEmrPopup) ──
  const FieldLabel = ({ label, required = false }: { label: string; required?: boolean }) => (
    <Typography
      sx={{
        fontSize: "0.8rem",
        fontWeight: 600,
        color: tokens.textSecondary,
        mb: 0.7,
        display: "flex",
        alignItems: "center",
        gap: 0.3,
      }}
    >
      {label}
      {required && (
        <Box component="span" sx={{ color: tokens.accent }}>*</Box>
      )}
    </Typography>
  );

  const defaultValues = useCallback(() => {
    if (defaultValue) {
      const processedValue = { ...defaultValue };
      if (
        editCommission &&
        Array.isArray(processedValue.productId) &&
        processedValue.productId.length === 1
      ) {
        processedValue.productId = processedValue.productId[0];
      }
      reset(processedValue);
    }
  }, [defaultValue, reset, editCommission]);

  useEffect(() => {
    defaultValues();
  }, [defaultValues]);

  const headerConfig = {
    add: {
      icon: <PackageSearch size={20} color={tokens.accent} strokeWidth={1.8} />,
      title: "Add Agency Product Service Fee",
      subtitle: "Choose how you'd like to add the agency product service fee",
    },
    edit: {
      icon: <PackageSearch size={20} color={tokens.accent} strokeWidth={1.8} />,
      title: "Edit Agency Product Service Fee",
      subtitle: "",
    },
  };

  const currentHeader = editCommission ? headerConfig.edit : headerConfig.add;

  return (
    <Dialog open={open} PaperProps={{ sx: paperSx(false) }}>
      {/* ── Header ── */}
      <Box sx={headerSx}>
        <Box sx={iconBoxSx(tokens.accent)}>{currentHeader.icon}</Box>
        <Box sx={{ flex: 1 }}>
          <Typography
            fontWeight={700}
            sx={{
              fontSize: "1.1rem",
              color: tokens.textPrimary,
              lineHeight: 1.3,
            }}
          >
            {currentHeader.title}
          </Typography>
          {currentHeader.subtitle && (
            <Typography
              sx={{ fontSize: "0.9rem", color: tokens.textSecondary, mt: 0.15 }}
            >
              {currentHeader.subtitle}
            </Typography>
          )}
        </Box>
      </Box>

      <form onSubmit={handleSubmit(submitCommission)}>
        <Box sx={bodySx}>
          <Grid container spacing={2}>
            {/* Product(s) */}
            <Grid item xs={12} style={{ marginTop: "5px" }}>
              <FieldLabel
                label={editCommission ? "Product" : "Product(s)"}
                required
              />
              <Box sx={dropdownWrapSx(false)}>
                {editCommission ? (
                  <CustomDropdownField
                    name="productId"
                    control={control}
                    options={productOptions}
                    label=""
                    rules={{
                      required: "Product is required.",
                      validate: {
                        notZero: (value: number) =>
                          value !== 0 || "Product is required.",
                      },
                    }}
                  />
                ) : (
                  <CustomMultipleDropDown
                    name="productId"
                    control={control}
                    options={productOptions}
                    label=""
                    rules={{
                      required: "Product is required.",
                      validate: {
                        notZero: (value: number) =>
                          value !== 0 || "Product is required.",
                      },
                    }}
                    disabled={editCommission}
                  />
                )}
              </Box>
            </Grid>

            {/* Commission % */}
            <Grid item xs={12}>
              <FieldLabel label="Service Rate (%)" required />
              <CustomTextField
                control={control}
                controllerName="commissionRate"
                size="small"
                label=""
                rules={{
                  required: "Service Rate is required.",
                  pattern: {
                    value: /^[0-9]+(\.[0-9]+)?$/,
                    message: "Invalid Service Rate.",
                  },
                }}
                sx={inputFieldSx(false)}
              />
            </Grid>

            {/* Commission Pay Terms */}
            <Grid item xs={12}>
              <FieldLabel label="Service Fee Pay Terms" />
              <CustomTextField
                control={control}
                controllerName="commissionPayTerms"
                size="small"
                label=""
                sx={inputFieldSx(false)}
              />
            </Grid>

            {/* Associate Type */}
            {isCommTypeOverride && (
              <Grid item xs={12} style={{ marginTop: "5px" }}>
                <FieldLabel label="Associate Type" required />
                <Box sx={dropdownWrapSx(false)}>
                  <CustomDropdownField
                    name="associateType"
                    control={control}
                    size="small"
                    label=""
                    options={associateOptions}
                    rules={{ required: "Associate Type is required." }}
                    onChangeSelect={(data) => {
                      if (data) {
                        if (data.value === "PRACTICE") {
                          setSelectedAssociateType(data.value);
                          setValue("associateAgencyId", undefined);
                          setValue("associateAccountRepId", undefined);
                        } else if (data.value === "ACCOUNTREP") {
                          setSelectedAssociateType(data.value);
                          setValue("associateAgencyId", undefined);
                          setValue("associatePracticeId", undefined);
                        } else if (data.value === "AGENCY") {
                          setSelectedAssociateType(data.value);
                          setValue("associatePracticeId", undefined);
                          setValue("associateAccountRepId", undefined);
                        } else {
                          setSelectedAssociateType("");
                          setValue("associateAgencyId", undefined);
                          setValue("associateAccountRepId", undefined);
                          setValue("associatePracticeId", undefined);
                        }
                      } else {
                        setSelectedAssociateType("");
                        setValue("associateAgencyId", undefined);
                        setValue("associateAccountRepId", undefined);
                        setValue("associatePracticeId", undefined);
                      }
                    }}
                    disabled={openFrom === "PracticeEditView"}
                  />
                </Box>
              </Grid>
            )}

            {/* Associate Practice */}
            {isCommTypeOverride &&
              selectedAssociateTypeOption === "PRACTICE" && (
                <Grid item xs={12} style={{ marginTop: "5px" }}>
                  <FieldLabel label="Associate Practice" required />
                  <Box sx={dropdownWrapSx(false)}>
                    <CustomDropdownField
                      name="associatePracticeId"
                      control={control}
                      options={practiceListOptions}
                      label=""
                      rules={{ required: "Associate Practice is required." }}
                    />
                  </Box>
                </Grid>
              )}

            {/* Associate Agency */}
            {isCommTypeOverride && selectedAssociateTypeOption === "AGENCY" && (
              <Grid item xs={12} style={{ marginTop: "5px" }}>
                <FieldLabel label="Associate Agency" required />
                <Box sx={dropdownWrapSx(false)}>
                  <CustomDropdownField
                    name="associateAgencyId"
                    control={control}
                    options={associateAgencyOptions}
                    label=""
                    rules={{ required: "Associate Agency is required." }}
                  />
                </Box>
              </Grid>
            )}

            {/* Associate Account Representative */}
            {isCommTypeOverride &&
              selectedAssociateTypeOption === "ACCOUNTREP" && (
                <Grid item xs={12} style={{ marginTop: "5px" }}>
                  <FieldLabel
                    label="Associate Account Representative"
                    required
                  />
                  <Box sx={dropdownWrapSx(false)}>
                    <CustomDropdownField
                      name="associateAccountRepId"
                      control={control}
                      options={agencyExecutivesOptions}
                      label=""
                      rules={{
                        required:
                          "Associate Account Representative is required.",
                      }}
                    />
                  </Box>
                </Grid>
              )}

            {/* Effective Date */}
            <Grid item xs={12}>
              <FieldLabel label="Effective Date" required />
              <Box sx={datepickerWrapSx(false)}>
                <CustomDatePicker
                  control={control}
                  size="small"
                  controllerName="effectiveDate"
                  label=""
                  rules={{ required: "Effective Date is required." }}
                />
              </Box>
            </Grid>

            {/* Inactive Date */}
            <Grid item xs={12}>
              <FieldLabel label="Inactive Date" />
              <Box sx={datepickerWrapSx(false)}>
                <CustomDatePicker
                  control={control}
                  size="small"
                  controllerName="inactiveDate"
                  label=""
                />
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* ── Footer ── */}
        <Box sx={footerSx}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 1.5,
              flexDirection: { xs: "column", sm: "row" },
            }}
          >
            <Button
              variant="contained"
              startIcon={<Save size={18} />}
              onClick={handleSubmit(submitCommission)}
              sx={{
                ...primaryBtnSx,
                color: "#ffffff",
                px: 3,
                "& .MuiTypography-root": { color: "#ffffff" },
                "& .MuiButton-startIcon": { color: "#ffffff" },
              }}
            >
              {editCommission ? "Update Service Fee" : "Save Service Fee"}
            </Button>

            <Button
              variant="outlined"
              startIcon={<CancelIcon />}
              onClick={onCloseAction}
              sx={{ ...cancelBtnSx, px: 3 }}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      </form>
    </Dialog>
  );
};

export default AddProductCommissionModal;